import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  User,
  Mail,
  Shield,
  ShieldCheck,
  Crown,
  UserCheck,
  Eye,
  Save,
  AlertCircle,
  CheckCircle,
  Loader2,
  Sparkles,
  Edit
} from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { getInitials } from '../lib/utils';
import { User as AdminUser } from '../types';
import { toast } from 'sonner';

interface EditUserModalProps {
  user: AdminUser | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (updatedUser: AdminUser) => Promise<void>;
}

interface UserFormData {
  name: string;
  email: string;
  role: 'super_admin' | 'manager' | 'support' | 'viewer';
  isActive: boolean;
}

interface FormErrors {
  name?: string;
  email?: string;
  role?: string;
  general?: string;
}

const roleOptions = [
  {
    value: 'super_admin',
    label: 'Super Admin',
    description: 'Full system access',
    icon: Crown,
    color: 'bg-indigo-100 text-indigo-800'
  },
  {
    value: 'manager',
    label: 'Manager', 
    description: 'Business operations',
    icon: ShieldCheck,
    color: 'bg-purple-100 text-purple-800'
  },
  {
    value: 'support',
    label: 'Support',
    description: 'Customer service',
    icon: UserCheck,
    color: 'bg-blue-100 text-blue-800'
  },
  {
    value: 'viewer',
    label: 'Viewer',
    description: 'Read-only access',
    icon: Eye,
    color: 'bg-gray-100 text-gray-800'
  }
];

export function EditUserModal({ user, isOpen, onClose, onSave }: EditUserModalProps) {
  const [formData, setFormData] = useState<UserFormData | null>(null);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});

  // Initialize form data when user changes
  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        email: user.email || '',
        role: user.role || 'viewer',
        isActive: user.isActive ?? false
      });
      setErrors({}); // Clear errors when user changes
    }
  }, [user]);

  if (!user || !formData) return null;

  // Helper functions
  const getRoleConfig = (role: string) => {
    const roleOption = roleOptions.find(r => r.value === role);
    return roleOption || {
      color: 'bg-gray-100 text-gray-800', 
      icon: Shield,
      label: 'Unknown',
      description: 'Unknown role'
    };
  };

  // Validation
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name?.trim()) {
      newErrors.name = 'Name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    }

    if (!formData.email?.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.role) {
      newErrors.role = 'Role is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSave = async () => {
    if (!validateForm()) {
      toast.error('Please fix the errors before saving');
      return;
    }

    setSaving(true);
    setErrors({});

    try {
      const updatedUser: AdminUser = {
        ...user,
        name: formData.name,
        email: formData.email,
        role: formData.role,
        isActive: formData.isActive
      };

      await onSave(updatedUser);
      toast.success('User updated successfully!');
      onClose();
    } catch (error) {
      console.error('Error updating user:', error);
      let errorMessage = 'Failed to update user. Please try again.';
      
      if (error instanceof Error) {
        if (error.message.includes('email-already-in-use')) {
          errorMessage = 'An account with this email already exists.';
          setErrors({ email: 'This email is already in use' });
        } else if (error.message.includes('permission-denied')) {
          errorMessage = 'You do not have permission to update this user.';
        } else if (error.message.includes('invalid-email')) {
          errorMessage = 'Please enter a valid email address.';
          setErrors({ email: 'Invalid email format' });
        }
      }
      
      setErrors({ general: errorMessage });
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  // Handle input changes
  const handleInputChange = (field: keyof UserFormData, value: string | boolean) => {
    setFormData(prev => prev ? { ...prev, [field]: value } : null);
    
    // Clear error when user starts typing
    if (errors[field as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  // Handle modal close
  const handleClose = () => {
    if (saving) return; // Prevent closing while saving
    
    setErrors({});
    onClose();
  };

  const roleConfig = getRoleConfig(formData.role);
  const RoleIcon = roleConfig.icon;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md z-[10001] flex items-center justify-center p-4" style={{ margin: 0, padding: '1rem', top: 0, left: 0, right: 0, bottom: 0 }}>
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ 
              type: "spring",
              duration: 0.5,
              bounce: 0.3 
            }}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden"
          >
            {/* Enhanced Header */}
            <div className="relative bg-gradient-to-r from-amber-600 to-orange-600 p-6 flex-shrink-0">
              <div className="absolute inset-0 bg-gradient-to-r from-amber-600/20 to-transparent"></div>
              <div className="relative flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <motion.div 
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.2, type: "spring", bounce: 0.6 }}
                    className="h-12 w-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/30"
                  >
                    <span className="text-white font-bold text-lg">{getInitials(user.name)}</span>
                  </motion.div>
                  <div>
                    <motion.h2 
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.1 }}
                      className="text-xl font-bold text-white"
                    >
                      Edit User Profile
                    </motion.h2>
                    <motion.p 
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.2 }}
                      className="text-white/80 text-sm mt-1"
                    >
                      Update {user.name}'s information and permissions
                    </motion.p>
                  </div>
                </div>
                <motion.div
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={handleClose}
                    disabled={saving}
                    className="text-white hover:bg-white/20 h-10 w-10 rounded-full"
                  >
                    <X className="h-5 w-5" />
                  </Button>
                </motion.div>
              </div>
            </div>

            {/* Enhanced Content */}
            <div className="flex-1 overflow-y-auto bg-gradient-to-b from-gray-50/50 to-white">
              <div className="p-6 space-y-6">
                {/* General Error */}
                {errors.general && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-red-800"
                  >
                    <AlertCircle className="h-4 w-4 flex-shrink-0" />
                    <p className="text-sm">{errors.general}</p>
                  </motion.div>
                )}

                {/* Basic Information */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  <Card className="border border-gray-200 shadow-sm rounded-xl">
                    <CardHeader className="pb-3">
                      <CardTitle className="flex items-center gap-2 text-base">
                        <User className="w-4 h-4 text-amber-600" />
                        Basic Information
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {/* Name */}
                      <div>
                        <Label htmlFor="name" className="text-sm font-medium text-gray-700">
                          Full Name <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="name"
                          type="text"
                          value={formData.name}
                          onChange={(e) => handleInputChange('name', e.target.value)}
                          className={`mt-1 rounded-xl border-gray-200 focus:border-amber-500 focus:ring-amber-500/20 ${errors.name ? 'border-red-300 focus:border-red-500' : ''}`}
                          placeholder="Enter full name"
                          disabled={saving}
                        />
                        {errors.name && (
                          <div className="flex items-center gap-1 mt-1">
                            <AlertCircle className="w-4 h-4 text-red-500" />
                            <p className="text-sm text-red-600">{errors.name}</p>
                          </div>
                        )}
                      </div>

                      {/* Email */}
                      <div>
                        <Label htmlFor="email" className="text-sm font-medium text-gray-700">
                          Email Address <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="email"
                          type="email"
                          value={formData.email}
                          onChange={(e) => handleInputChange('email', e.target.value)}
                          className={`mt-1 rounded-xl border-gray-200 focus:border-amber-500 focus:ring-amber-500/20 ${errors.email ? 'border-red-300 focus:border-red-500' : ''}`}
                          placeholder="Enter email address"
                          disabled={saving}
                        />
                        {errors.email && (
                          <div className="flex items-center gap-1 mt-1">
                            <AlertCircle className="w-4 h-4 text-red-500" />
                            <p className="text-sm text-red-600">{errors.email}</p>
                          </div>
                        )}
                      </div>

                      {/* User ID (Read-only) */}
                      <div>
                        <Label className="text-sm font-medium text-gray-700">User ID</Label>
                        <div className="mt-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl">
                          <p className="text-sm text-gray-600 font-mono">{user.id}</p>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">User ID cannot be changed</p>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>

                {/* Role & Permissions */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                >
                  <Card className="border border-gray-200 shadow-sm rounded-xl">
                    <CardHeader className="pb-3">
                      <CardTitle className="flex items-center gap-2 text-base">
                        <Shield className="w-4 h-4 text-amber-600" />
                        Role & Permissions
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {/* Role Selection */}
                      <div>
                        <Label htmlFor="role" className="text-sm font-medium text-gray-700">
                          User Role <span className="text-red-500">*</span>
                        </Label>
                        <Select
                          value={formData.role}
                          onValueChange={(value) => handleInputChange('role', value as UserFormData['role'])}
                          disabled={saving}
                        >
                          <SelectTrigger className={`mt-1 rounded-xl border-gray-200 focus:border-amber-500 focus:ring-amber-500/20 text-left ${errors.role ? 'border-red-300 focus:border-red-500' : ''}`}>
                            <SelectValue placeholder="Select role" />
                          </SelectTrigger>
                          <SelectContent>
                            {roleOptions.map((role) => {
                              const IconComponent = role.icon;
                              return (
                                <SelectItem key={role.value} value={role.value}>
                                  <div className="flex items-center gap-2">
                                    <IconComponent className="w-4 h-4" />
                                    <div>
                                      <p className="font-medium">{role.label}</p>
                                      <p className="text-xs text-gray-500">{role.description}</p>
                                    </div>
                                  </div>
                                </SelectItem>
                              );
                            })}
                          </SelectContent>
                        </Select>
                        {errors.role && (
                          <div className="flex items-center gap-1 mt-1">
                            <AlertCircle className="w-4 h-4 text-red-500" />
                            <p className="text-sm text-red-600">{errors.role}</p>
                          </div>
                        )}
                      </div>

                      {/* Current Role Display */}
                      <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-xl">
                        <Badge className={`${roleConfig.color} border rounded-lg`}>
                          <RoleIcon className="w-3 h-3 mr-1" />
                          {roleConfig.label}
                        </Badge>
                        <span className="text-sm text-gray-600">{roleConfig.description}</span>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>

                {/* Account Status */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                >
                  <Card className="border border-gray-200 shadow-sm rounded-xl">
                    <CardHeader className="pb-3">
                      <CardTitle className="flex items-center gap-2 text-base">
                        <CheckCircle className="w-4 h-4 text-amber-600" />
                        Account Status
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {/* Active Status */}
                      <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                        <div className="space-y-1">
                          <Label className="text-sm font-medium text-gray-700">Account Status</Label>
                          <p className="text-sm text-gray-600">
                            {formData.isActive ? 'User can access the admin portal' : 'User access is disabled'}
                          </p>
                        </div>
                        <Switch
                          checked={formData.isActive}
                          onCheckedChange={(checked) => handleInputChange('isActive', checked)}
                          disabled={saving}
                        />
                      </div>

                      <div>
                        <Badge className={formData.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                          {formData.isActive ? 'Active' : 'Inactive'}
                        </Badge>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>

                {/* Info Box */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 }}
                  className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-xl"
                >
                  <Mail className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-blue-800">
                    <p className="font-medium">Important Notice</p>
                    <p>Changes to user permissions will take effect immediately. Inactive users will be logged out of all sessions.</p>
                  </div>
                </motion.div>
              </div>
            </div>

            {/* Enhanced Footer */}
            <div className="p-6 bg-gray-50 border-t border-gray-100 flex-shrink-0">
              <div className="flex gap-3">
                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} className="flex-1">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleClose}
                    disabled={saving}
                    className="w-full px-6 h-11 rounded-xl font-medium"
                  >
                    Cancel
                  </Button>
                </motion.div>
                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} className="flex-1">
                  <Button
                    onClick={handleSave}
                    disabled={saving}
                    className="w-full bg-amber-600 hover:bg-amber-700 text-white px-6 h-11 rounded-xl font-semibold shadow-lg shadow-amber-600/25"
                  >
                    {saving ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Saving Changes...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Save Changes
                      </>
                    )}
                  </Button>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
} 